/**
 * TeeTimeMarketplace - Golf Tee Time Marketplace
 * 
 * Two views:
 * 1. Golfer View - Browse & book hot deals on tee times
 * 2. Course Portal - Post tee times, create deals, manage bookings
 */
import { useState, useEffect, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { format, addDays, parseISO } from 'date-fns';
import {
  Clock, MapPin, DollarSign, Users, Calendar, ChevronLeft, ChevronRight,
  Flame, Tag, Search, Filter, Star, Check, X, Plus, Building2, 
  CreditCard, TrendingUp, Zap, Sun, Moon, ArrowRight, Percent,
  Trophy, RefreshCw, ExternalLink, Settings, BarChart3
} from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from './ui/dialog';
import { useLocationContext } from '../hooks/LocationProvider';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

// Deal type badges
const DEAL_BADGES = {
  twilight: { label: 'Twilight', icon: <Sun className="w-3 h-3" />, color: 'bg-orange-500' },
  last_minute: { label: 'Last Minute', icon: <Clock className="w-3 h-3" />, color: 'bg-red-500' },
  weekday: { label: 'Weekday', icon: <Calendar className="w-3 h-3" />, color: 'bg-blue-500' },
  weekend_am: { label: 'Weekend AM', icon: <Sun className="w-3 h-3" />, color: 'bg-purple-500' },
  fill_the_sheet: { label: 'Flash Deal', icon: <Zap className="w-3 h-3" />, color: 'bg-yellow-500' }
};

// ============ GOLFER VIEW - Browse & Book Deals ============
const GolferMarketplace = ({ user }) => {
  const navigate = useNavigate();
  const { latitude, longitude, hasLocation } = useLocationContext();
  const [deals, setDeals] = useState({ featured_deals: [], deals: [] });
  const [slots, setSlots] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedDate, setSelectedDate] = useState(format(new Date(), 'yyyy-MM-dd'));
  const [showFilters, setShowFilters] = useState(false);
  const [maxPrice, setMaxPrice] = useState(null);
  const [dealsOnly, setDealsOnly] = useState(true);
  const [selectedSlot, setSelectedSlot] = useState(null);
  const [bookingPlayers, setBookingPlayers] = useState(1);
  const [showBookingModal, setShowBookingModal] = useState(false);

  // Fetch hot deals
  const fetchDeals = useCallback(async () => {
    try {
      const params = new URLSearchParams();
      if (hasLocation && latitude && longitude) {
        params.append('lat', latitude);
        params.append('lng', longitude);
      }
      params.append('date', selectedDate);
      
      const response = await axios.get(`${API}/marketplace/deals?${params}`);
      setDeals(response.data);
    } catch (error) {
      console.error('Error fetching deals:', error);
    }
  }, [hasLocation, latitude, longitude, selectedDate]);

  // Fetch available tee times
  const fetchSlots = useCallback(async () => {
    try {
      const params = new URLSearchParams();
      if (hasLocation && latitude && longitude) {
        params.append('lat', latitude);
        params.append('lng', longitude);
      }
      params.append('date', selectedDate);
      if (maxPrice) params.append('max_price', maxPrice);
      if (dealsOnly) params.append('deals_only', 'true');
      
      const response = await axios.get(`${API}/marketplace/browse?${params}`);
      setSlots(response.data.courses || []);
    } catch (error) {
      console.error('Error fetching slots:', error);
    } finally {
      setLoading(false);
    }
  }, [hasLocation, latitude, longitude, selectedDate, maxPrice, dealsOnly]);

  useEffect(() => {
    fetchDeals();
    fetchSlots();
  }, [fetchDeals, fetchSlots]);

  // Book a tee time
  const handleBook = async () => {
    if (!selectedSlot) return;
    
    try {
      const response = await axios.post(
        `${API}/marketplace/book/${selectedSlot.slot_id}?players=${bookingPlayers}`
      );
      
      toast.success(`Reserved ${bookingPlayers} spot(s)! Complete payment to confirm.`);
      setShowBookingModal(false);
      setSelectedSlot(null);
      
      // TODO: Redirect to payment
      // navigate(`/marketplace/checkout/${response.data.booking.booking_id}`);
      
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to book');
    }
  };

  // Date picker
  const dates = Array.from({ length: 7 }, (_, i) => {
    const date = addDays(new Date(), i);
    return {
      value: format(date, 'yyyy-MM-dd'),
      label: i === 0 ? 'Today' : i === 1 ? 'Tomorrow' : format(date, 'EEE'),
      date: format(date, 'MMM d')
    };
  });

  return (
    <div className="min-h-screen bg-gray-950">
      {/* Header */}
      <div className="sticky top-0 z-20 bg-gray-950/95 backdrop-blur border-b border-gray-800">
        <div className="px-4 py-3">
          <div className="flex items-center justify-between mb-3">
            <button onClick={() => navigate('/home')} className="p-2 -ml-2">
              <ChevronLeft className="w-6 h-6 text-gray-400" />
            </button>
            <h1 className="text-lg font-bold text-white flex items-center gap-2">
              <Flame className="w-5 h-5 text-orange-400" />
              Tee Time Deals
            </h1>
            <button 
              onClick={() => setShowFilters(!showFilters)}
              className="p-2 text-gray-400 hover:text-white"
            >
              <Filter className="w-5 h-5" />
            </button>
          </div>

          {/* Date Selector */}
          <div className="flex gap-2 overflow-x-auto pb-2 -mx-4 px-4 scrollbar-hide">
            {dates.map((d) => (
              <button
                key={d.value}
                onClick={() => setSelectedDate(d.value)}
                className={`flex-shrink-0 px-3 py-2 rounded-lg text-center transition-all ${
                  selectedDate === d.value
                    ? 'bg-orange-500 text-white'
                    : 'bg-gray-800 text-gray-400 hover:bg-gray-700'
                }`}
              >
                <div className="text-xs font-medium">{d.label}</div>
                <div className="text-xs opacity-75">{d.date}</div>
              </button>
            ))}
          </div>
        </div>

        {/* Filters Panel */}
        {showFilters && (
          <div className="px-4 pb-3 border-t border-gray-800 pt-3">
            <div className="flex gap-3 items-center">
              <button
                onClick={() => setDealsOnly(!dealsOnly)}
                className={`px-3 py-1.5 rounded-full text-xs font-medium transition-all ${
                  dealsOnly ? 'bg-orange-500 text-white' : 'bg-gray-800 text-gray-400'
                }`}
              >
                🔥 Deals Only
              </button>
              <select
                value={maxPrice || ''}
                onChange={(e) => setMaxPrice(e.target.value ? Number(e.target.value) : null)}
                className="bg-gray-800 text-white text-xs rounded-lg px-3 py-1.5 border-0"
              >
                <option value="">Any Price</option>
                <option value="50">Under $50</option>
                <option value="75">Under $75</option>
                <option value="100">Under $100</option>
              </select>
            </div>
          </div>
        )}
      </div>

      {/* Content */}
      <div className="p-4 space-y-6">
        {/* Featured Deals */}
        {deals.featured_deals.length > 0 && (
          <section>
            <h2 className="text-sm font-semibold text-orange-400 mb-3 flex items-center gap-2">
              <Zap className="w-4 h-4" /> FEATURED DEALS
            </h2>
            <div className="space-y-3">
              {deals.featured_deals.map((deal) => (
                <DealCard key={deal.deal_id} deal={deal} onSelect={setSelectedSlot} />
              ))}
            </div>
          </section>
        )}

        {/* All Deals */}
        {deals.deals.length > 0 && (
          <section>
            <h2 className="text-sm font-semibold text-gray-400 mb-3 flex items-center gap-2">
              <Flame className="w-4 h-4" /> HOT DEALS NEAR YOU
            </h2>
            <div className="space-y-3">
              {deals.deals.map((deal) => (
                <DealCard key={deal.deal_id} deal={deal} onSelect={setSelectedSlot} />
              ))}
            </div>
          </section>
        )}

        {/* Available Tee Times by Course */}
        {slots.length > 0 && (
          <section>
            <h2 className="text-sm font-semibold text-gray-400 mb-3 flex items-center gap-2">
              <Clock className="w-4 h-4" /> AVAILABLE TEE TIMES
            </h2>
            <div className="space-y-4">
              {slots.map((course) => (
                <CourseSlotCard 
                  key={course.course_id} 
                  course={course} 
                  onSelectSlot={(slot) => {
                    setSelectedSlot(slot);
                    setShowBookingModal(true);
                  }}
                />
              ))}
            </div>
          </section>
        )}

        {/* Empty State */}
        {!loading && deals.deals.length === 0 && deals.featured_deals.length === 0 && slots.length === 0 && (
          <div className="text-center py-12">
            <Clock className="w-12 h-12 text-gray-600 mx-auto mb-3" />
            <p className="text-gray-400">No tee times available for this date</p>
            <p className="text-gray-500 text-sm mt-1">Try selecting a different date</p>
          </div>
        )}

        {/* Course Portal Link */}
        <div className="mt-8 p-4 bg-gradient-to-r from-blue-900/30 to-indigo-900/30 rounded-xl border border-blue-500/30">
          <div className="flex items-center gap-3">
            <Building2 className="w-8 h-8 text-blue-400" />
            <div className="flex-1">
              <h3 className="text-white font-semibold">Own a Golf Course?</h3>
              <p className="text-gray-400 text-sm">Post your tee times & reach more golfers</p>
            </div>
            <Button
              onClick={() => navigate('/marketplace/course')}
              className="bg-blue-500 hover:bg-blue-600"
            >
              Course Portal
            </Button>
          </div>
        </div>
      </div>

      {/* Booking Modal */}
      <Dialog open={showBookingModal} onOpenChange={setShowBookingModal}>
        <DialogContent className="bg-gray-900 border-gray-700">
          <DialogHeader>
            <DialogTitle className="text-white">Book Tee Time</DialogTitle>
          </DialogHeader>
          
          {selectedSlot && (
            <div className="space-y-4">
              <div className="bg-gray-800 rounded-lg p-4">
                <h3 className="text-white font-semibold">{selectedSlot.course_name}</h3>
                <div className="flex items-center gap-4 mt-2 text-gray-400 text-sm">
                  <span className="flex items-center gap-1">
                    <Calendar className="w-4 h-4" />
                    {format(parseISO(selectedSlot.date), 'MMM d, yyyy')}
                  </span>
                  <span className="flex items-center gap-1">
                    <Clock className="w-4 h-4" />
                    {selectedSlot.time}
                  </span>
                </div>
              </div>

              {/* Player Count */}
              <div>
                <label className="text-gray-400 text-sm mb-2 block">Number of Players</label>
                <div className="flex gap-2">
                  {[1, 2, 3, 4].map((num) => (
                    <button
                      key={num}
                      onClick={() => setBookingPlayers(num)}
                      disabled={num > (selectedSlot.spots_available - selectedSlot.spots_booked)}
                      className={`flex-1 py-2 rounded-lg font-medium transition-all ${
                        bookingPlayers === num
                          ? 'bg-orange-500 text-white'
                          : num > (selectedSlot.spots_available - selectedSlot.spots_booked)
                          ? 'bg-gray-800 text-gray-600 cursor-not-allowed'
                          : 'bg-gray-800 text-gray-300 hover:bg-gray-700'
                      }`}
                    >
                      {num}
                    </button>
                  ))}
                </div>
              </div>

              {/* Price Breakdown */}
              <div className="bg-gray-800 rounded-lg p-4 space-y-2">
                <div className="flex justify-between text-gray-400">
                  <span>Price per player</span>
                  <span>${selectedSlot.booking_price?.toFixed(2)}</span>
                </div>
                {selectedSlot.deal_price && selectedSlot.green_fee > selectedSlot.deal_price && (
                  <div className="flex justify-between text-green-400 text-sm">
                    <span>You save</span>
                    <span>-${((selectedSlot.green_fee - selectedSlot.deal_price) * bookingPlayers).toFixed(2)}</span>
                  </div>
                )}
                <div className="flex justify-between text-white font-semibold pt-2 border-t border-gray-700">
                  <span>Total</span>
                  <span>${(selectedSlot.booking_price * bookingPlayers).toFixed(2)}</span>
                </div>
                {selectedSlot.includes_cart && (
                  <p className="text-xs text-gray-500">Includes cart</p>
                )}
              </div>
            </div>
          )}

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowBookingModal(false)}>
              Cancel
            </Button>
            <Button onClick={handleBook} className="bg-orange-500 hover:bg-orange-600">
              Reserve & Pay ${selectedSlot ? (selectedSlot.booking_price * bookingPlayers).toFixed(2) : '0'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

// Deal Card Component
const DealCard = ({ deal, onSelect }) => {
  const badge = DEAL_BADGES[deal.deal_type];
  
  return (
    <div className="bg-gradient-to-r from-gray-800 to-gray-850 rounded-xl p-4 border border-gray-700 hover:border-orange-500/50 transition-all">
      <div className="flex items-start gap-3">
        <div className="w-16 h-16 bg-gradient-to-br from-orange-500/20 to-red-500/20 rounded-lg flex items-center justify-center">
          <Flame className="w-8 h-8 text-orange-400" />
        </div>
        
        <div className="flex-1">
          <div className="flex items-center gap-2 mb-1">
            {badge && (
              <span className={`px-2 py-0.5 rounded-full text-xs font-medium text-white flex items-center gap-1 ${badge.color}`}>
                {badge.icon} {badge.label}
              </span>
            )}
            {deal.savings_percent >= 25 && (
              <span className="px-2 py-0.5 rounded-full text-xs font-medium bg-green-500 text-white">
                {deal.savings_percent}% OFF
              </span>
            )}
          </div>
          
          <h3 className="text-white font-semibold">{deal.course_name}</h3>
          <p className="text-gray-400 text-sm">{deal.name}</p>
          
          <div className="flex items-center gap-3 mt-2 text-sm">
            <span className="text-gray-500 flex items-center gap-1">
              <Calendar className="w-3 h-3" />
              {format(parseISO(deal.date), 'MMM d')}
            </span>
            <span className="text-gray-500 flex items-center gap-1">
              <Clock className="w-3 h-3" />
              {deal.start_time} - {deal.end_time}
            </span>
            {deal.distance_miles && (
              <span className="text-gray-500 flex items-center gap-1">
                <MapPin className="w-3 h-3" />
                {deal.distance_miles} mi
              </span>
            )}
          </div>
        </div>
        
        <div className="text-right">
          <div className="text-gray-500 line-through text-sm">${deal.regular_price}</div>
          <div className="text-2xl font-bold text-orange-400">${deal.deal_price}</div>
          <div className="text-xs text-gray-500">per player</div>
          <Button 
            size="sm" 
            className="mt-2 bg-orange-500 hover:bg-orange-600"
            onClick={() => onSelect(deal)}
          >
            Book
          </Button>
        </div>
      </div>
    </div>
  );
};

// Course Slot Card
const CourseSlotCard = ({ course, onSelectSlot }) => {
  return (
    <div className="bg-gray-800 rounded-xl overflow-hidden">
      <div className="p-3 border-b border-gray-700">
        <div className="flex items-center justify-between">
          <div>
            <h3 className="text-white font-semibold">{course.course_name}</h3>
            {course.distance_miles && (
              <p className="text-gray-500 text-sm flex items-center gap-1">
                <MapPin className="w-3 h-3" /> {course.distance_miles} miles away
              </p>
            )}
          </div>
        </div>
      </div>
      
      <div className="p-3 flex gap-2 overflow-x-auto">
        {course.slots.slice(0, 6).map((slot) => (
          <button
            key={slot.slot_id}
            onClick={() => onSelectSlot(slot)}
            className="flex-shrink-0 bg-gray-700 hover:bg-gray-600 rounded-lg px-3 py-2 text-center transition-all"
          >
            <div className="text-white font-medium">{slot.time}</div>
            <div className="text-xs text-gray-400">
              ${slot.booking_price}
            </div>
            {slot.deal_price && (
              <div className="text-xs text-green-400">Deal!</div>
            )}
          </button>
        ))}
        {course.slots.length > 6 && (
          <div className="flex-shrink-0 bg-gray-700/50 rounded-lg px-3 py-2 flex items-center text-gray-500 text-sm">
            +{course.slots.length - 6} more
          </div>
        )}
      </div>
    </div>
  );
};

// ============ COURSE PORTAL - Manage Inventory ============
const CoursePortal = ({ user }) => {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('inventory');
  const [course, setCourse] = useState(null);
  const [inventory, setInventory] = useState({ slots: [], stats: {} });
  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedDate, setSelectedDate] = useState(format(new Date(), 'yyyy-MM-dd'));
  const [showPostModal, setShowPostModal] = useState(false);
  const [showDealModal, setShowDealModal] = useState(false);
  const [stripeStatus, setStripeStatus] = useState(null);

  // Fetch course data
  useEffect(() => {
    const fetchCourse = async () => {
      try {
        const response = await axios.get(`${API}/proshop/my-course`);
        setCourse(response.data);
      } catch (error) {
        // No course registered
        setCourse(null);
      } finally {
        setLoading(false);
      }
    };
    fetchCourse();
  }, []);

  // Fetch inventory
  useEffect(() => {
    if (!course) return;
    
    const fetchInventory = async () => {
      try {
        const response = await axios.get(`${API}/marketplace/inventory?date=${selectedDate}`);
        setInventory(response.data);
      } catch (error) {
        console.error('Error fetching inventory:', error);
      }
    };
    fetchInventory();
  }, [course, selectedDate]);

  // Fetch bookings
  useEffect(() => {
    if (!course) return;
    
    const fetchBookings = async () => {
      try {
        const response = await axios.get(`${API}/marketplace/course-bookings?date=${selectedDate}`);
        setBookings(response.data.bookings || []);
      } catch (error) {
        console.error('Error fetching bookings:', error);
      }
    };
    fetchBookings();
  }, [course, selectedDate]);

  // Check Stripe status
  useEffect(() => {
    if (!course) return;
    
    const checkStripe = async () => {
      try {
        const response = await axios.get(`${API}/marketplace/course/stripe-status`);
        setStripeStatus(response.data);
      } catch (error) {
        console.error('Error checking Stripe:', error);
      }
    };
    checkStripe();
  }, [course]);

  // Start Stripe onboarding
  const handleStripeConnect = async () => {
    try {
      const response = await axios.post(`${API}/marketplace/course/stripe-connect`);
      window.location.href = response.data.onboarding_url;
    } catch (error) {
      toast.error('Failed to start payment setup');
    }
  };

  // Post bulk tee times
  const handlePostTimes = async (formData) => {
    try {
      const response = await axios.post(`${API}/marketplace/inventory/bulk`, formData);
      toast.success(`Posted ${response.data.slots_created} tee times!`);
      setShowPostModal(false);
      // Refresh inventory
      const inv = await axios.get(`${API}/marketplace/inventory?date=${selectedDate}`);
      setInventory(inv.data);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to post times');
    }
  };

  // Create hot deal
  const handleCreateDeal = async (formData) => {
    try {
      const response = await axios.post(`${API}/marketplace/hot-deals`, formData);
      toast.success(`Hot Deal "${response.data.deal.name}" is live!`);
      setShowDealModal(false);
      // Refresh inventory
      const inv = await axios.get(`${API}/marketplace/inventory?date=${selectedDate}`);
      setInventory(inv.data);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create deal');
    }
  };

  // No course registered
  if (!loading && !course) {
    return (
      <div className="min-h-screen bg-gray-950 p-4">
        <button onClick={() => navigate('/home')} className="flex items-center text-gray-400 mb-6">
          <ChevronLeft className="w-5 h-5" /> Back
        </button>
        
        <div className="text-center py-12">
          <Building2 className="w-16 h-16 text-blue-400 mx-auto mb-4" />
          <h1 className="text-2xl font-bold text-white mb-2">Course Portal</h1>
          <p className="text-gray-400 mb-6">Register your course to start posting tee times</p>
          <Button data-testid="marketplace-register-course" onClick={() => navigate('/proshop')} className="bg-blue-500 hover:bg-blue-600">
            Register Your Course
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-950">
      {/* Header */}
      <div className="sticky top-0 z-20 bg-gray-950/95 backdrop-blur border-b border-gray-800">
        <div className="px-4 py-3">
          <div className="flex items-center justify-between mb-2">
            <button onClick={() => navigate('/home')} className="p-2 -ml-2">
              <ChevronLeft className="w-6 h-6 text-gray-400" />
            </button>
            <div className="text-center">
              <h1 className="text-lg font-bold text-white">{course?.name || 'Course Portal'}</h1>
              <p className="text-xs text-gray-500">Par Seeker Marketplace</p>
            </div>
            <Button data-testid="marketplace-course-settings" variant="ghost" size="sm" onClick={() => navigate('/book?workspace=proshop')}>
              <Settings className="w-4 h-4" />
            </Button>
          </div>

          {/* Tabs */}
          <div className="flex gap-1 bg-gray-800 rounded-lg p-1">
            {[
              { id: 'inventory', label: 'Inventory', icon: <Clock className="w-4 h-4" /> },
              { id: 'bookings', label: 'Bookings', icon: <Users className="w-4 h-4" /> },
              { id: 'analytics', label: 'Analytics', icon: <BarChart3 className="w-4 h-4" /> }
            ].map((tab) => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`flex-1 flex items-center justify-center gap-1.5 py-2 rounded-md text-sm font-medium transition-all ${
                  activeTab === tab.id
                    ? 'bg-blue-500 text-white'
                    : 'text-gray-400 hover:text-white'
                }`}
              >
                {tab.icon} {tab.label}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Stripe Connect Banner */}
      {stripeStatus && stripeStatus.status !== 'active' && (
        <div className="mx-4 mt-4 p-4 bg-gradient-to-r from-yellow-900/30 to-orange-900/30 rounded-xl border border-yellow-500/30">
          <div className="flex items-center gap-3">
            <CreditCard className="w-8 h-8 text-yellow-400" />
            <div className="flex-1">
              <h3 className="text-white font-semibold">Set Up Payments</h3>
              <p className="text-gray-400 text-sm">Connect your bank to receive payouts from bookings</p>
            </div>
            <Button onClick={handleStripeConnect} className="bg-yellow-500 hover:bg-yellow-600 text-black">
              Connect
            </Button>
          </div>
        </div>
      )}

      {/* Content */}
      <div className="p-4">
        {activeTab === 'inventory' && (
          <InventoryTab
            inventory={inventory}
            selectedDate={selectedDate}
            onDateChange={setSelectedDate}
            onPostTimes={() => setShowPostModal(true)}
            onCreateDeal={() => setShowDealModal(true)}
          />
        )}

        {activeTab === 'bookings' && (
          <BookingsTab bookings={bookings} />
        )}

        {activeTab === 'analytics' && (
          <AnalyticsTab stats={inventory.stats} />
        )}
      </div>

      {/* Post Times Modal */}
      <PostTimesModal
        isOpen={showPostModal}
        onClose={() => setShowPostModal(false)}
        onSubmit={handlePostTimes}
        course={course}
      />

      {/* Create Deal Modal */}
      <CreateDealModal
        isOpen={showDealModal}
        onClose={() => setShowDealModal(false)}
        onSubmit={handleCreateDeal}
        course={course}
      />
    </div>
  );
};

// Inventory Tab
const InventoryTab = ({ inventory, selectedDate, onDateChange, onPostTimes, onCreateDeal }) => {
  const dates = Array.from({ length: 7 }, (_, i) => {
    const date = addDays(new Date(), i);
    return {
      value: format(date, 'yyyy-MM-dd'),
      label: i === 0 ? 'Today' : format(date, 'EEE'),
      date: format(date, 'd')
    };
  });

  return (
    <div className="space-y-4">
      {/* Stats Bar */}
      <div className="grid grid-cols-3 gap-3">
        <div className="bg-gray-800 rounded-lg p-3 text-center">
          <div className="text-2xl font-bold text-white">{inventory.stats?.live_slots || 0}</div>
          <div className="text-xs text-gray-500">Live Slots</div>
        </div>
        <div className="bg-gray-800 rounded-lg p-3 text-center">
          <div className="text-2xl font-bold text-green-400">{inventory.stats?.booked_spots || 0}</div>
          <div className="text-xs text-gray-500">Booked</div>
        </div>
        <div className="bg-gray-800 rounded-lg p-3 text-center">
          <div className="text-2xl font-bold text-orange-400">{inventory.stats?.fill_rate || 0}%</div>
          <div className="text-xs text-gray-500">Fill Rate</div>
        </div>
      </div>

      {/* Date Selector */}
      <div className="flex gap-2 overflow-x-auto pb-2 -mx-4 px-4">
        {dates.map((d) => (
          <button
            key={d.value}
            onClick={() => onDateChange(d.value)}
            className={`flex-shrink-0 w-12 py-2 rounded-lg text-center transition-all ${
              selectedDate === d.value
                ? 'bg-blue-500 text-white'
                : 'bg-gray-800 text-gray-400 hover:bg-gray-700'
            }`}
          >
            <div className="text-xs">{d.label}</div>
            <div className="text-lg font-bold">{d.date}</div>
          </button>
        ))}
      </div>

      {/* Action Buttons */}
      <div className="flex gap-3">
        <Button onClick={onPostTimes} className="flex-1 bg-blue-500 hover:bg-blue-600">
          <Plus className="w-4 h-4 mr-2" /> Post Tee Times
        </Button>
        <Button onClick={onCreateDeal} className="flex-1 bg-orange-500 hover:bg-orange-600">
          <Flame className="w-4 h-4 mr-2" /> Create Hot Deal
        </Button>
      </div>

      {/* Inventory List */}
      <div className="space-y-2">
        {inventory.slots?.length === 0 ? (
          <div className="text-center py-8 text-gray-500">
            <Clock className="w-10 h-10 mx-auto mb-2 opacity-50" />
            <p>No tee times posted for this date</p>
            <p className="text-sm">Click "Post Tee Times" to add inventory</p>
          </div>
        ) : (
          inventory.slots?.map((slot) => (
            <div
              key={slot.slot_id}
              className={`flex items-center justify-between p-3 rounded-lg ${
                slot.status === 'live' ? 'bg-gray-800' : 'bg-gray-800/50'
              }`}
            >
              <div className="flex items-center gap-3">
                <div className="text-center">
                  <div className="text-white font-semibold">{slot.time}</div>
                  <div className={`text-xs ${
                    slot.status === 'live' ? 'text-green-400' : 
                    slot.status === 'full' ? 'text-yellow-400' : 'text-gray-500'
                  }`}>
                    {slot.status === 'live' ? '🟢 Live' : 
                     slot.status === 'full' ? '⬛ Full' : slot.status}
                  </div>
                </div>
                <div>
                  <div className="text-gray-400 text-sm">
                    {slot.spots_booked}/{slot.spots_available} booked
                  </div>
                  {slot.deal_price && (
                    <span className="text-xs bg-orange-500/20 text-orange-400 px-2 py-0.5 rounded">
                      Deal: ${slot.deal_price}
                    </span>
                  )}
                </div>
              </div>
              <div className="text-right">
                <div className="text-white font-medium">${slot.booking_price}</div>
                <div className="text-xs text-gray-500">
                  You get: ${slot.course_payout}
                </div>
              </div>
            </div>
          ))
        )}
      </div>
    </div>
  );
};

// Bookings Tab
const BookingsTab = ({ bookings }) => {
  return (
    <div className="space-y-3">
      {bookings.length === 0 ? (
        <div className="text-center py-8 text-gray-500">
          <Users className="w-10 h-10 mx-auto mb-2 opacity-50" />
          <p>No bookings yet</p>
        </div>
      ) : (
        bookings.map((booking) => (
          <div key={booking.booking_id} className="bg-gray-800 rounded-lg p-4">
            <div className="flex items-center justify-between mb-2">
              <div className="flex items-center gap-2">
                <div className="w-8 h-8 bg-blue-500/20 rounded-full flex items-center justify-center text-blue-400 font-semibold">
                  {booking.user_name?.charAt(0) || 'G'}
                </div>
                <div>
                  <div className="text-white font-medium">{booking.user_name}</div>
                  <div className="text-xs text-gray-500">{booking.user_email}</div>
                </div>
              </div>
              <span className={`px-2 py-1 rounded text-xs ${
                booking.status === 'confirmed' ? 'bg-green-500/20 text-green-400' :
                booking.status === 'pending_payment' ? 'bg-yellow-500/20 text-yellow-400' :
                'bg-gray-700 text-gray-400'
              }`}>
                {booking.status}
              </span>
            </div>
            <div className="flex items-center justify-between text-sm text-gray-400">
              <span>{booking.time} • {booking.players} player(s)</span>
              <span className="text-white font-medium">${booking.total_price}</span>
            </div>
            <div className="text-xs text-gray-600 mt-1">
              Confirmation: {booking.confirmation_code}
            </div>
          </div>
        ))
      )}
    </div>
  );
};

// Analytics Tab
const AnalyticsTab = ({ stats }) => {
  return (
    <div className="space-y-4">
      <div className="bg-gradient-to-r from-blue-900/30 to-indigo-900/30 rounded-xl p-6 border border-blue-500/30">
        <h3 className="text-blue-400 font-semibold mb-4">This Week's Performance</h3>
        <div className="grid grid-cols-2 gap-4">
          <div>
            <div className="text-3xl font-bold text-white">{stats?.total_slots || 0}</div>
            <div className="text-gray-400 text-sm">Total Slots Posted</div>
          </div>
          <div>
            <div className="text-3xl font-bold text-green-400">{stats?.booked_spots || 0}</div>
            <div className="text-gray-400 text-sm">Golfers Booked</div>
          </div>
          <div>
            <div className="text-3xl font-bold text-orange-400">{stats?.fill_rate || 0}%</div>
            <div className="text-gray-400 text-sm">Fill Rate</div>
          </div>
          <div>
            <div className="text-3xl font-bold text-white">$0</div>
            <div className="text-gray-400 text-sm">Revenue</div>
          </div>
        </div>
      </div>

      <div className="bg-gray-800 rounded-xl p-4">
        <h4 className="text-white font-semibold mb-3">Tips to Increase Bookings</h4>
        <ul className="space-y-2 text-sm text-gray-400">
          <li className="flex items-start gap-2">
            <Check className="w-4 h-4 text-green-400 mt-0.5" />
            Create twilight deals to fill afternoon slots
          </li>
          <li className="flex items-start gap-2">
            <Check className="w-4 h-4 text-green-400 mt-0.5" />
            Post last-minute deals for same-day availability
          </li>
          <li className="flex items-start gap-2">
            <Check className="w-4 h-4 text-green-400 mt-0.5" />
            Feature your deals to reach more golfers
          </li>
        </ul>
      </div>
    </div>
  );
};

// Post Times Modal
const PostTimesModal = ({ isOpen, onClose, onSubmit, course }) => {
  const [formData, setFormData] = useState({
    date: format(new Date(), 'yyyy-MM-dd'),
    start_time: '06:00',
    end_time: '18:00',
    interval_minutes: 10,
    spots_per_slot: 4,
    green_fee: course?.green_fee || 65,
    cart_fee: course?.cart_fee || 20,
    includes_cart: true
  });

  const handleSubmit = () => {
    onSubmit(formData);
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="bg-gray-900 border-gray-700 max-w-md">
        <DialogHeader>
          <DialogTitle className="text-white">Post Tee Times</DialogTitle>
        </DialogHeader>
        
        <div className="space-y-4">
          <div>
            <label className="text-gray-400 text-sm">Date</label>
            <Input
              type="date"
              value={formData.date}
              onChange={(e) => setFormData({ ...formData, date: e.target.value })}
              className="bg-gray-800 border-gray-700 text-white"
            />
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-gray-400 text-sm">Start Time</label>
              <Input
                type="time"
                value={formData.start_time}
                onChange={(e) => setFormData({ ...formData, start_time: e.target.value })}
                className="bg-gray-800 border-gray-700 text-white"
              />
            </div>
            <div>
              <label className="text-gray-400 text-sm">End Time</label>
              <Input
                type="time"
                value={formData.end_time}
                onChange={(e) => setFormData({ ...formData, end_time: e.target.value })}
                className="bg-gray-800 border-gray-700 text-white"
              />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-gray-400 text-sm">Green Fee ($)</label>
              <Input
                type="number"
                value={formData.green_fee}
                onChange={(e) => setFormData({ ...formData, green_fee: Number(e.target.value) })}
                className="bg-gray-800 border-gray-700 text-white"
              />
            </div>
            <div>
              <label className="text-gray-400 text-sm">Cart Fee ($)</label>
              <Input
                type="number"
                value={formData.cart_fee}
                onChange={(e) => setFormData({ ...formData, cart_fee: Number(e.target.value) })}
                className="bg-gray-800 border-gray-700 text-white"
              />
            </div>
          </div>

          <div className="flex items-center justify-between p-3 bg-gray-800 rounded-lg">
            <span className="text-gray-300">Include Cart in Price</span>
            <button
              onClick={() => setFormData({ ...formData, includes_cart: !formData.includes_cart })}
              className={`w-12 h-6 rounded-full transition-all ${
                formData.includes_cart ? 'bg-blue-500' : 'bg-gray-600'
              }`}
            >
              <div className={`w-5 h-5 bg-white rounded-full transition-all ${
                formData.includes_cart ? 'ml-6' : 'ml-0.5'
              }`} />
            </button>
          </div>

          <div className="p-3 bg-blue-500/10 rounded-lg border border-blue-500/30">
            <div className="flex justify-between text-sm">
              <span className="text-gray-400">Platform Fee (10%)</span>
              <span className="text-gray-300">${((formData.green_fee + (formData.includes_cart ? 0 : formData.cart_fee)) * 0.1).toFixed(2)}</span>
            </div>
            <div className="flex justify-between text-sm mt-1">
              <span className="text-gray-400">You Receive</span>
              <span className="text-white font-semibold">${((formData.green_fee + (formData.includes_cart ? 0 : formData.cart_fee)) * 0.9).toFixed(2)}</span>
            </div>
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={handleSubmit} className="bg-blue-500 hover:bg-blue-600">
            Post Times
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

// Create Deal Modal
const CreateDealModal = ({ isOpen, onClose, onSubmit, course }) => {
  const [formData, setFormData] = useState({
    name: 'Twilight Special',
    deal_type: 'twilight',
    date: format(new Date(), 'yyyy-MM-dd'),
    start_time: '14:00',
    end_time: '18:00',
    regular_price: course?.green_fee || 65,
    deal_price: 45,
    includes_cart: true,
    includes_range: false,
    max_spots: 16,
    featured: false,
    push_notification: false
  });

  const savings = formData.regular_price - formData.deal_price;
  const savingsPercent = Math.round((savings / formData.regular_price) * 100);

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="bg-gray-900 border-gray-700 max-w-md max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="text-white flex items-center gap-2">
            <Flame className="w-5 h-5 text-orange-400" /> Create Hot Deal
          </DialogTitle>
        </DialogHeader>
        
        <div className="space-y-4">
          <div>
            <label className="text-gray-400 text-sm">Deal Name</label>
            <Input
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              placeholder="e.g., Twilight Special"
              className="bg-gray-800 border-gray-700 text-white"
            />
          </div>

          <div>
            <label className="text-gray-400 text-sm">Deal Type</label>
            <div className="grid grid-cols-2 gap-2 mt-1">
              {Object.entries(DEAL_BADGES).map(([type, badge]) => (
                <button
                  key={type}
                  onClick={() => setFormData({ ...formData, deal_type: type })}
                  className={`p-2 rounded-lg text-sm flex items-center gap-2 transition-all ${
                    formData.deal_type === type
                      ? `${badge.color} text-white`
                      : 'bg-gray-800 text-gray-400 hover:bg-gray-700'
                  }`}
                >
                  {badge.icon} {badge.label}
                </button>
              ))}
            </div>
          </div>

          <div>
            <label className="text-gray-400 text-sm">Date</label>
            <Input
              type="date"
              value={formData.date}
              onChange={(e) => setFormData({ ...formData, date: e.target.value })}
              className="bg-gray-800 border-gray-700 text-white"
            />
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-gray-400 text-sm">Start Time</label>
              <Input
                type="time"
                value={formData.start_time}
                onChange={(e) => setFormData({ ...formData, start_time: e.target.value })}
                className="bg-gray-800 border-gray-700 text-white"
              />
            </div>
            <div>
              <label className="text-gray-400 text-sm">End Time</label>
              <Input
                type="time"
                value={formData.end_time}
                onChange={(e) => setFormData({ ...formData, end_time: e.target.value })}
                className="bg-gray-800 border-gray-700 text-white"
              />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-gray-400 text-sm">Regular Price ($)</label>
              <Input
                type="number"
                value={formData.regular_price}
                onChange={(e) => setFormData({ ...formData, regular_price: Number(e.target.value) })}
                className="bg-gray-800 border-gray-700 text-white"
              />
            </div>
            <div>
              <label className="text-gray-400 text-sm">Deal Price ($)</label>
              <Input
                type="number"
                value={formData.deal_price}
                onChange={(e) => setFormData({ ...formData, deal_price: Number(e.target.value) })}
                className="bg-gray-800 border-gray-700 text-white"
              />
            </div>
          </div>

          {/* Savings Preview */}
          <div className="p-3 bg-green-500/10 rounded-lg border border-green-500/30">
            <div className="flex items-center justify-between">
              <span className="text-green-400 font-semibold">{savingsPercent}% OFF</span>
              <span className="text-white">Save ${savings} per golfer</span>
            </div>
          </div>

          <div className="space-y-2">
            <label className="flex items-center justify-between p-3 bg-gray-800 rounded-lg cursor-pointer">
              <span className="text-gray-300">Include Cart</span>
              <button
                onClick={() => setFormData({ ...formData, includes_cart: !formData.includes_cart })}
                className={`w-12 h-6 rounded-full transition-all ${
                  formData.includes_cart ? 'bg-blue-500' : 'bg-gray-600'
                }`}
              >
                <div className={`w-5 h-5 bg-white rounded-full transition-all ${
                  formData.includes_cart ? 'ml-6' : 'ml-0.5'
                }`} />
              </button>
            </label>

            <label className="flex items-center justify-between p-3 bg-gray-800 rounded-lg cursor-pointer">
              <span className="text-gray-300">⭐ Feature This Deal</span>
              <button
                onClick={() => setFormData({ ...formData, featured: !formData.featured })}
                className={`w-12 h-6 rounded-full transition-all ${
                  formData.featured ? 'bg-orange-500' : 'bg-gray-600'
                }`}
              >
                <div className={`w-5 h-5 bg-white rounded-full transition-all ${
                  formData.featured ? 'ml-6' : 'ml-0.5'
                }`} />
              </button>
            </label>
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={() => onSubmit(formData)} className="bg-orange-500 hover:bg-orange-600">
            <Flame className="w-4 h-4 mr-2" /> Launch Deal
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

// ============ MAIN COMPONENT ============
const TeeTimeMarketplace = ({ user, isCourseView = false }) => {
  const [searchParams] = useSearchParams();
  const viewType = searchParams.get('view') || (isCourseView ? 'course' : 'golfer');

  if (viewType === 'course' || isCourseView) {
    return <CoursePortal user={user} />;
  }

  return <GolferMarketplace user={user} />;
};

export default TeeTimeMarketplace;
